import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react'

const ToastCtx = createContext(null)

const CFG = {
  success: { icon: CheckCircle2, cls: 'from-emerald-500 to-teal-600',   shadow: 'shadow-emerald-500/40' },
  error:   { icon: XCircle,       cls: 'from-rose-500 to-red-600',       shadow: 'shadow-rose-500/40'    },
  info:    { icon: Info,          cls: 'from-blue-500 to-indigo-600',    shadow: 'shadow-blue-500/40'    },
  warning: { icon: AlertTriangle, cls: 'from-amber-500 to-orange-500',   shadow: 'shadow-amber-500/40'   },
}

function ToastItem({ toast, onClose }) {
  const { icon: Icon, cls, shadow } = CFG[toast.type] || CFG.info
  return (
    <div
      className={`flex items-start gap-3 bg-gradient-to-r ${cls} text-white px-4 py-3.5 rounded-2xl shadow-xl ${shadow} border border-white/20 min-w-[300px] max-w-xs pointer-events-auto`}
      style={{ animation: 'toastIn 0.35s cubic-bezier(0.34,1.56,0.64,1)' }}
    >
      <Icon size={18} className="shrink-0 mt-0.5" />
      <p className="flex-1 text-sm font-semibold leading-snug">{toast.message}</p>
      <button onClick={onClose} className="shrink-0 p-0.5 rounded-lg hover:bg-white/20 transition-colors ml-1">
        <X size={14} />
      </button>
    </div>
  )
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef({})

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id])
    setToasts(p => p.filter(t => t.id !== id))
  }, [])

  const toast = useCallback((message, type = 'info', duration = 3500) => {
    const id = Date.now() + Math.random()
    setToasts(p => [...p.slice(-4), { id, message, type }])
    timers.current[id] = setTimeout(() => dismiss(id), duration)
  }, [dismiss])

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="fixed bottom-6 right-6 z-[600] flex flex-col gap-2.5 pointer-events-none">
        {toasts.map(t => <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />)}
      </div>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(80px) scale(0.9); }
          to   { opacity: 1; transform: translateX(0)   scale(1);   }
        }
      `}</style>
    </ToastCtx.Provider>
  )
}

export const useToast = () => useContext(ToastCtx)
